import React, { useState, useMemo, useEffect, useContext, startTransition } from 'react'
import { Modal } from 'reactstrap'
import { useToasts } from 'react-toast-notifications'
import { SelectIdGroupContext } from '_context/SelectGroupContext'
import dashBoardApi from '_api/dashBoard'
import { getGroupIdSelected } from '_utils/localData'
import useLoadingState from '_hooks/useLoadingState'
import {
  FaTimes,
  FaShoppingBag,
  FaClock,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa'
import moment from 'moment'
import { openWhatsApp } from '../../helper/whatsapp'
import { formatCurrency } from '../../helper/formatNumber'
import './style.scss'
import { formatSingaporePhone } from '../../helper/formatSingaporePhone'
import { formatVietNamPhone } from '../../helper/formatVietNamPhone'

const formatDate = (timestamp, stringFallback = 'N/A') => {
  if (!timestamp) return stringFallback
  if (typeof timestamp === 'string') return timestamp
  const date = moment.unix(timestamp)
  return date.isValid() ? date.format('DD MM YYYY, hh:mm A') : stringFallback
}

export default function ListOrderUnpaidModal({
  isOpen = false,
  toggle,
  buyer,
  orders,
}) {
  const [expandedOrderId, setExpandedOrderId] = useState(null)
  const [selectedOrderRefs, setSelectedOrderRefs] = useState(new Set())
  const { isLoading, startLoading, stopLoading } = useLoadingState()

  const context = useContext(SelectIdGroupContext)
  const { groupId: contextGroupId } = context?.infoGroupSelected || {}
  const groupId = contextGroupId || getGroupIdSelected()
  const { addToast } = useToasts()

  const buyerName = buyer?.bName || ''
  const buyerPhone = buyer?.phone || ''
  const buyerEmail = buyer?.email || ''
  const buyerPhoto = buyer?.photoUrl || ''

  // Use passed orders prop or buyer's unpaidOrders array
  const rawOrdersList = orders || buyer?.unpaidOrders || []

  useEffect(() => {
    if (!isOpen) {
      setSelectedOrderRefs(new Set())
      setExpandedOrderId(null)
    }
  }, [isOpen])

  const ordersList = useMemo(
    () =>
      (rawOrdersList || []).map((order) => {
        const ref = order?.ref
        const eventTitle = order?.eventTitle
        const amount = order?.amount || 0
        const discountAmount = order?.discountAmount || 0
        const originalAmount = amount + discountAmount
        const createdDate = formatDate(order?.createdAt)

        const items = (order.items || []).map((item, itemIdx) => ({
          id: item.id || `item-${itemIdx}`,
          name: item?.pName,
          qty: item?.pQuantity,
          price: item?.pPrice,
        }))

        const totalQuantity =
          order.totalQuantity ||
          items.reduce((sum, item) => sum + (item.qty || 0), 0)

        return {
          id: ref,
          ref,
          eventTitle,
          amount,
          discountAmount,
          originalAmount,
          createdDate,
          totalQuantity,
          items,
          uid: order?.uid,
        }
      }),
    [rawOrdersList]
  )

  const totalAmount = useMemo(
    () =>
      buyer?.totalUnpaidAmount ??
      ordersList.reduce((sum, item) => sum + (item.amount || 0), 0),
    [buyer?.totalUnpaidAmount, ordersList]
  )

  const isAllSelected =
    ordersList.length > 0 && selectedOrderRefs.size === ordersList.length
  const isIndeterminate =
    selectedOrderRefs.size > 0 && selectedOrderRefs.size < ordersList.length

  const handleSelectAll = () => {
    startTransition(() => {
      if (isAllSelected) {
        setSelectedOrderRefs(new Set())
      } else {
        setSelectedOrderRefs(new Set(ordersList.map((o) => o.ref)))
      }
    })
  }

  const handleToggleSelectOrder = (ref) => {
    startTransition(() => {
      setSelectedOrderRefs((prev) => {
        const next = new Set(prev)
        next.has(ref) ? next.delete(ref) : next.add(ref)
        return next
      })
    })
  }

  const toggleExpandOrder = (e, id) => {
    e.stopPropagation()
    setExpandedOrderId((prev) => (prev === id ? null : id))
  }

  const handleRemindClick = async () => {
    if (selectedOrderRefs.size === 0 || isLoading('REMIND')) return
    if (!groupId) {
      addToast('Group ID is missing', {
        appearance: 'warning',
        autoDismiss: true,
      })
      return
    }

    startLoading('REMIND')
    try {
      await dashBoardApi.remindBuyerPayment({
        groupId,
        refs: [...selectedOrderRefs],
      })
      addToast('Payment reminder sent successfully', {
        appearance: 'success',
        autoDismiss: true,
      })
      setSelectedOrderRefs(new Set())
      if (toggle) toggle()
    } catch (err) {
      console.error('remindBuyerPayment error:', err)
      addToast(
        err?.msgResp || err?.message || 'Failed to send payment reminder',
        {
          appearance: 'error',
          autoDismiss: true,
        }
      )
    } finally {
      stopLoading('REMIND')
    }
  }

  const handleSendWhatsapp = () => {
    if (selectedOrderRefs.size === 0) return

    const cleanBuyerPhone = formatSingaporePhone(buyerPhone) || formatVietNamPhone(buyerPhone)

    if (!cleanBuyerPhone) {
      addToast('Invalid buyer phone number', {
        appearance: 'warning',
        autoDismiss: true,
      })
      return
    }

    const selectedOrders = ordersList.filter((order) =>
      selectedOrderRefs.has(order.ref))

    const baseUrl = process.env.REACT_APP_APP_URL || window.location.origin
    const buyerUid = buyer?.uid || ''

    const orderItemsText = selectedOrders
      .map((order, idx) => {
        const uidParam = order.uid || buyerUid
        const orderUrl = `${baseUrl}/payment-order?ref=${order.ref}${uidParam ? `&uid=${uidParam}` : ''}`

        return [
          `${idx + 1}. Event name: ${order.eventTitle || 'N/A'}`,
          `• Order ref: #${order.ref}`,
          `• Amount due: ${formatCurrency(order.amount)}`,
          `• Order detail:\n${orderUrl}`,
        ].join('\n')
      })
      .join('\n\n')

    const message = [
      `Hello ${buyerName || 'buyer'},`,
      `This is a friendly reminder that you currently have ${selectedOrders.length} unpaid orders with the details below:`,
      '',
      orderItemsText,
      '',
      'Please check and complete the payment as soon as possible. Thank you very much!',
    ].join('\n')

    openWhatsApp({
      phone: cleanBuyerPhone,
      message,
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className='list-order-unpaid-modal'
      centered
    >
      <div className='list-order-unpaid-modal__container'>
        {/* Header */}
        <div className='list-order-unpaid-modal__header'>
          <div className='list-order-unpaid-modal__header-info'>
            <h3 className='list-order-unpaid-modal__title'>Pending Payments</h3>
            <span className='list-order-unpaid-modal__subtitle'>
              Detailed list of pending payments by event
            </span>
          </div>
          <button
            type='button'
            className='list-order-unpaid-modal__close-btn'
            onClick={toggle}
          >
            <FaTimes />
          </button>
        </div>

        {/* Buyer Profile Summary Bar */}
        <div className='list-order-unpaid-modal__buyer-bar'>
          <div className='list-order-unpaid-modal__buyer-avatar-box'>
            {buyerPhoto ? (
              <img
                src={buyerPhoto}
                alt={buyerName}
                className='list-order-unpaid-modal__avatar'
              />
            ) : (
              <div className='list-order-unpaid-modal__avatar-initial'>
                {buyerName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className='list-order-unpaid-modal__buyer-details'>
            <span className='list-order-unpaid-modal__buyer-name'>
              {buyerName}
            </span>
            <span className='list-order-unpaid-modal__buyer-contact'>
              {buyerPhone} {buyerEmail ? `• ${buyerEmail}` : ''}
            </span>
          </div>
          <div className='list-order-unpaid-modal__buyer-stat'>
            <span className='list-order-unpaid-modal__stat-label'>
              Total Outstanding
            </span>
            <span className='list-order-unpaid-modal__stat-value'>
              ${totalAmount.toFixed(2)}
            </span>
          </div>
        </div>
        {ordersList.length > 0 && (
          <div
            className='list-order-unpaid-modal__select-all'
            onClick={handleSelectAll}
          >
            <div
              className={`list-order-unpaid-modal__checkbox ${
                isAllSelected ? 'checked' : isIndeterminate ? 'indeterminate' : ''
              }`}
            >
              {isAllSelected && (
                <svg
                  className='list-order-unpaid-modal__checkbox-icon'
                  viewBox='0 0 12 10'
                  fill='none'
                >
                  <path
                    d='M1 5L4.5 8.5L11 1'
                    stroke='white'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              )}
              {isIndeterminate && (
                <span className='list-order-unpaid-modal__checkbox-dash' />
              )}
            </div>
            <span className='list-order-unpaid-modal__select-all-label'>
              Select All
            </span>
          </div>
        )}
        {/* List of Orders */}
        <div className='list-order-unpaid-modal__list'>
          {ordersList.length === 0 ? (
            <div className='list-order-unpaid-modal__empty'>
              <FaShoppingBag className='list-order-unpaid-modal__empty-icon' />
              <p>No unpaid orders found for this buyer</p>
            </div>
          ) : (
            ordersList.map((order) => {
              const isExpanded = expandedOrderId === order.id
              const isSelected = selectedOrderRefs.has(order.ref)
              return (
                <div
                  key={order.id}
                  className={`list-order-unpaid-modal__card ${
                    isSelected ? 'selected' : ''
                  }`}
                  onClick={() => handleToggleSelectOrder(order.ref)}
                >
                  {/* Card Header (Radio, Event Title & Total Amount) */}
                  <div className='list-order-unpaid-modal__card-header'>
                    <div className='list-order-unpaid-modal__checkbox-box'>
                      <div
                        className={`list-order-unpaid-modal__checkbox ${
                          isSelected ? 'checked' : ''
                        }`}
                      >
                        {isSelected && (
                          <svg className='list-order-unpaid-modal__checkbox-icon' viewBox='0 0 12 10' fill='none'>
                            <path d='M1 5L4.5 8.5L11 1' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div className='list-order-unpaid-modal__event-box'>
                      <span className='list-order-unpaid-modal__order-no'>
                        Ref: {order.ref}
                      </span>
                      <h4 className='list-order-unpaid-modal__event-title'>
                        {order.eventTitle}
                      </h4>
                    </div>
                    <div className='list-order-unpaid-modal__card-price-box'>
                      <span className='list-order-unpaid-modal__status-badge'>
                        Unpaid
                      </span>
                      <div className='list-order-unpaid-modal__price-wrapper'>
                        {order.discountAmount > 0 && (
                          <span className='list-order-unpaid-modal__original-price'>
                            ${order.originalAmount.toFixed(2)}
                          </span>
                        )}
                        <span className='list-order-unpaid-modal__card-price'>
                          ${order.amount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Date & Product Quantity Grid */}
                  <div className='list-order-unpaid-modal__meta-grid'>
                    {/* Order Creation Date */}
                    <div className='list-order-unpaid-modal__meta-item created-date'>
                      <div className='list-order-unpaid-modal__meta-icon-box'>
                        <FaClock />
                      </div>
                      <div className='list-order-unpaid-modal__meta-content'>
                        <span className='list-order-unpaid-modal__meta-label'>
                          Created Date
                        </span>
                        <span className='list-order-unpaid-modal__meta-value'>
                          {order.createdDate}
                        </span>
                      </div>
                    </div>

                    {/* Quantity */}
                    <div className='list-order-unpaid-modal__meta-item quantity'>
                      <div className='list-order-unpaid-modal__meta-icon-box'>
                        <FaShoppingBag />
                      </div>
                      <div className='list-order-unpaid-modal__meta-content'>
                        <span className='list-order-unpaid-modal__meta-label'>
                          Products
                        </span>
                        <span className='list-order-unpaid-modal__meta-value'>
                          {order.totalQuantity}{' '}
                          {order.totalQuantity > 1 ? 'items' : 'item'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Accordion / Expand Products Toggle */}
                  {order.items && order.items.length > 0 && (
                    <div className='list-order-unpaid-modal__toggle-products'>
                      <button
                        type='button'
                        className='list-order-unpaid-modal__toggle-btn'
                        onClick={(e) => toggleExpandOrder(e, order.id)}
                      >
                        <span>
                          {isExpanded
                            ? 'Hide Product Details'
                            : 'View Product Details'}
                        </span>
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                      </button>

                      {isExpanded && (
                        <div className='list-order-unpaid-modal__products-table'>
                          {order.items.map((item) => (
                            <div
                              key={item.id}
                              className='list-order-unpaid-modal__product-row'
                            >
                              <div className='list-order-unpaid-modal__product-info'>
                                <span className='list-order-unpaid-modal__product-name'>
                                  {item.name}
                                </span>
                                <span className='list-order-unpaid-modal__product-qty'>
                                  x{item.qty}
                                </span>
                              </div>
                              <span className='list-order-unpaid-modal__product-price'>
                                ${(item.price * item.qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div className='list-order-unpaid-modal__footer'>
          <div className='list-order-unpaid-modal__footer-summary'>
            <span className='list-order-unpaid-modal__footer-label'>
              Total ({ordersList.length}{' '}
              {ordersList.length > 1 ? 'orders' : 'order'}):
            </span>
            <span className='list-order-unpaid-modal__footer-amount'>
              ${totalAmount.toFixed(2)}
            </span>
          </div>
          <div className='list-order-unpaid-modal__actions'>
            <button
              type='button'
              className='list-order-unpaid-modal__btn-close'
              onClick={toggle}
            >
              Close
            </button>
            <button
              type='button'
              className='list-order-unpaid-modal__btn-remind'
              disabled={selectedOrderRefs.size === 0 || isLoading('REMIND')}
              onClick={handleRemindClick}
            >
              {isLoading('REMIND')
                ? 'Sending...'
                : `Send Email (${selectedOrderRefs.size})`}
            </button>
            <button
              type='button'
              className='list-order-unpaid-modal__btn-whatsapp'
              disabled={selectedOrderRefs.size === 0}
              onClick={handleSendWhatsapp}
            >
              {`Send WhatsApp (${selectedOrderRefs.size})`}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
